<template>
  <div class="page" v-loading="loading">
    <div class="card">
      <div class="header">
        <div class="title">系统设置</div>
        <div class="actions">
          <el-button v-if="!isEditing" type="primary" @click="startEdit">修改配置</el-button>
          <template v-else>
            <el-button @click="cancelEdit">取消</el-button>
            <el-button type="primary" @click="saveSettings" :loading="saving">保存配置</el-button>
          </template>
        </div>
      </div>
      
      <el-form :model="form" label-width="120px" class="form" :disabled="!isEditing">
        
        <el-tabs v-model="activeTab" type="card">
          <!-- 1. 基础设置 -->
          <el-tab-pane label="基础设置" name="basic">
            <div class="section-title">基础与外观</div>
            <el-form-item label="系统名称">
              <el-input v-model="form.system_name" placeholder="PsyScale"></el-input>
            </el-form-item>
            <el-form-item label="系统副标题">
              <el-input v-model="form.system_subtitle" placeholder="诊所测评系统"></el-input>
            </el-form-item>
            <el-form-item label="系统 Logo">
              <div class="upload-wrapper">
                <el-input v-model="form.system_logo" placeholder="图片 URL"></el-input>
                <el-upload
                  class="uploader"
                  action=""
                  :http-request="(p) => uploadImage(p, 'system_logo')"
                  :show-file-list="false">
                  <el-button size="small" type="primary">点击上传</el-button>
                </el-upload>
              </div>
              <div v-if="form.system_logo" class="preview">
                <img :src="form.system_logo" style="max-height: 40px">
              </div>
            </el-form-item>
            <el-form-item label="浏览器标题">
              <el-input v-model="form.browser_title" placeholder="心理测评系统"></el-input>
            </el-form-item>
            <el-form-item label="网页图标">
              <div class="upload-wrapper">
                <el-input v-model="form.favicon_url" placeholder="/favicon.ico"></el-input>
                <el-upload
                  class="uploader"
                  action=""
                  :http-request="(p) => uploadImage(p, 'favicon_url')"
                  :show-file-list="false">
                  <el-button size="small" type="primary">点击上传</el-button>
                </el-upload>
              </div>
              <div v-if="form.favicon_url" class="preview">
                <img :src="form.favicon_url" style="width: 32px; height: 32px">
              </div>
            </el-form-item>
          </el-tab-pane>

          <!-- 2. 界面文案 -->
          <el-tab-pane label="界面文案" name="text">
            <div class="section-title">登录页设置</div>
            <el-form-item label="登录页标题">
              <el-input v-model="form.login_title" placeholder="PsyScale 管理端"></el-input>
            </el-form-item>
            <el-form-item label="登录页副标题">
              <el-input v-model="form.login_subtitle" placeholder="登录后可管理来访者并开具测评"></el-input>
            </el-form-item>
            
            <div class="section-title">测评报告设置</div>
            <el-form-item label="报告主标题">
              <el-input v-model="form.report_title" placeholder="心理测评结果报告"></el-input>
            </el-form-item>
            <el-form-item label="报告副标题">
              <el-input v-model="form.report_subtitle" placeholder="PsyScale Clinical Assessment System"></el-input>
            </el-form-item>
            <el-form-item label="机构名称">
              <el-input v-model="form.institution_name" placeholder="某某心理诊所"></el-input>
            </el-form-item>
            <el-form-item label="页脚免责声明">
              <el-input type="textarea" v-model="form.report_disclaimer" placeholder="本报告仅供参考..."></el-input>
            </el-form-item>
          </el-tab-pane>

          <!-- 3. 权限管理 (新增) -->
          <el-tab-pane label="权限管理" name="permissions">
            <div class="section-title">管理员权限控制</div>
            <div style="margin-bottom: 16px; color: #666; font-size: 13px; padding-left: 20px;">
              此处控制"普通管理员"的权限范围。超级管理员拥有所有权限，不受此限制。
            </div>
            <el-form-item label="允许导出量表">
              <el-switch v-model="form.admin_can_export_scales_bool"></el-switch>
              <span style="margin-left: 10px; color: #999; font-size: 12px;">开启后，管理员可将量表导出为 JSON 文件</span>
            </el-form-item>
            <el-form-item label="允许导入量表">
              <el-switch v-model="form.admin_can_import_scales_bool"></el-switch>
              <span style="margin-left: 10px; color: #999; font-size: 12px;">开启后，管理员可从 JSON 文件导入新量表</span>
            </el-form-item>
            <el-form-item label="允许编辑量表">
              <el-switch v-model="form.admin_can_edit_scales_bool"></el-switch>
              <span style="margin-left: 10px; color: #999; font-size: 12px;">开启后，管理员可新建或修改量表内容</span>
            </el-form-item>
            <el-form-item label="允许删除量表">
              <el-switch v-model="form.admin_can_delete_scales_bool"></el-switch>
              <span style="margin-left: 10px; color: #ff4d4f; font-size: 12px;">⚠️ 高危操作：开启后，管理员可永久删除量表及其关联数据</span>
            </el-form-item>
          </el-tab-pane>

          <!-- 4. AI 设置 -->
          <el-tab-pane label="AI 设置" name="ai">
            <div class="section-title">AI 成本控制</div>
            <el-form-item label="启用AI功能">
              <el-switch v-model="form.ai_enabled_bool"></el-switch>
            </el-form-item>
            <el-form-item label="月Token上限">
              <el-input v-model="form.ai_monthly_token_limit" placeholder="2000000"></el-input>
            </el-form-item>
            <el-form-item label="全站暂停到">
              <el-date-picker
                v-model="form.ai_global_pause_until"
                type="datetime"
                placeholder="选择时间（留空则不暂停）"
                clearable
                format="yyyy-MM-dd HH:mm:ss"
                value-format="yyyy-MM-dd'T'HH:mm:ss">
              </el-date-picker>
            </el-form-item>
            <el-form-item label="全站并发上限">
              <el-input v-model="form.ai_concurrency_limit_global" placeholder="5"></el-input>
            </el-form-item>
            <el-form-item label="全站每日次数">
              <el-input v-model="form.ai_daily_global_requests_limit" placeholder="200"></el-input>
            </el-form-item>
            <el-form-item label="用户每日次数(来访者)">
              <el-input v-model="form.ai_daily_user_requests_limit_visitor" placeholder="20"></el-input>
            </el-form-item>
            <el-form-item label="用户每日次数(咨询师)">
              <el-input v-model="form.ai_daily_user_requests_limit_clinical" placeholder="10"></el-input>
            </el-form-item>
            <el-form-item label="用户每分钟次数">
              <el-input v-model="form.ai_minute_user_requests_limit" placeholder="3"></el-input>
            </el-form-item>

            <div class="section-title">协议管理</div>
            <el-form-item label="AI 使用协议">
              <el-button type="primary" size="small" @click="openAgreementPublish">发布新版本</el-button>
              <el-button type="text" size="small" @click="viewCurrentAgreement" style="margin-left: 12px;">查看当前版本</el-button>
              <div style="margin-top: 8px; color: var(--text-secondary); font-size: 12px;">
                发布新版本后，用户下次调用 AI 将被要求重新签署。
              </div>
            </el-form-item>
          </el-tab-pane>
        </el-tabs>

      </el-form>
    </div>

    <el-dialog :visible.sync="currentAgreementDialogVisible" title="当前 AI 使用协议" width="620px">
      <div v-loading="currentAgreementLoading">
        <div v-if="currentAgreement">
          <div style="margin-bottom: 16px; font-weight: bold;">
            版本：{{ currentAgreement.version }}
            <span style="font-weight: normal; font-size: 12px; color: #666; margin-left: 8px;">
              (生效时间: {{ formatDateTime(currentAgreement.effective_from) }})
            </span>
          </div>
          <div class="agreement-content">{{ currentAgreement.content }}</div>
        </div>
        <div v-else style="text-align: center; color: #999; padding: 30px;">
          暂无生效协议
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="currentAgreementDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog :visible.sync="agreementDialogVisible" title="发布 AI 使用协议新版本" width="720px">
      <el-form :model="agreementPublishForm" label-width="100px">
        <el-form-item label="版本号">
          <el-input v-model="agreementPublishForm.version" placeholder="例如 2026-02"></el-input>
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="agreementPublishForm.title"></el-input>
        </el-form-item>
        <el-form-item label="正文">
          <el-input type="textarea" :rows="10" v-model="agreementPublishForm.content" placeholder="协议正文"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="agreementDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="agreementPublishing" @click="publishAiAgreement">发布</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getAllSettings, publishAgreement, updateSettings, uploadSystemImage, getAgreementCurrent } from '@/api/settings'
import { notifyApiError } from '@/utils/notifyApiError'

export default {
  name: 'SystemSettings',
  data() {
    return {
      activeTab: 'basic',
      loading: false,
      saving: false,
      isEditing: false,
      originalForm: {}, // 用于取消时恢复
      form: {
        system_name: '',
        system_subtitle: '',
        system_logo: '',
        browser_title: '',
        favicon_url: '',
        login_title: '',
        login_subtitle: '',
        report_title: '',
        report_subtitle: '',
        institution_name: '',
        report_disclaimer: '',
        
        // Permissions
        admin_can_export_scales_bool: false,
        admin_can_import_scales_bool: false,
        admin_can_edit_scales_bool: false,
        admin_can_delete_scales_bool: false,
        
        // AI
        ai_enabled_bool: true,
        ai_monthly_token_limit: '',
        ai_global_pause_until: '',
        ai_concurrency_limit_global: '',
        ai_daily_global_requests_limit: '',
        ai_daily_user_requests_limit_visitor: '',
        ai_daily_user_requests_limit_clinical: '',
        ai_minute_user_requests_limit: '',
      }
      ,
      agreementDialogVisible: false,
      agreementPublishing: false,
      agreementPublishForm: {
        version: '',
        title: 'AI 功能使用协议',
        content: '',
      },
      currentAgreementDialogVisible: false,
      currentAgreementLoading: false,
      currentAgreement: null,
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const resp = await getAllSettings()
        // Convert list to dict
        const settings = {}
        resp.data.forEach(s => {
          settings[s.key] = s.value
        })
        
        // Merge into form
        const merged = { ...this.form, ...settings }
        merged.ai_enabled_bool = String(settings.ai_enabled || 'True') === 'True'
        merged.admin_can_export_scales_bool = String(settings.admin_can_export_scales || 'False') === 'True'
        merged.admin_can_import_scales_bool = String(settings.admin_can_import_scales || 'False') === 'True'
        merged.admin_can_edit_scales_bool = String(settings.admin_can_edit_scales || 'False') === 'True'
        merged.admin_can_delete_scales_bool = String(settings.admin_can_delete_scales || 'False') === 'True'
        
        this.form = merged
        this.originalForm = JSON.parse(JSON.stringify(this.form))
      } catch (e) {
        notifyApiError(e, '加载配置失败')
      } finally {
        this.loading = false
      }
    },
    async uploadImage(param, field) {
      try {
        const resp = await uploadSystemImage(param.file)
        this.form[field] = resp.data.url
        this.$message.success('上传成功')
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.error ? e.response.data.error : '上传失败'
        notifyApiError(e, msg)
      }
    },
    startEdit() {
      this.isEditing = true
    },
    cancelEdit() {
      this.isEditing = false
      this.form = JSON.parse(JSON.stringify(this.originalForm))
    },
    async saveSettings() {
      this.saving = true
      try {
        const payload = { ...this.form }
        payload.ai_enabled = this.form.ai_enabled_bool ? 'True' : 'False'
        delete payload.ai_enabled_bool
        
        payload.admin_can_export_scales = this.form.admin_can_export_scales_bool ? 'True' : 'False'
        delete payload.admin_can_export_scales_bool
        
        payload.admin_can_import_scales = this.form.admin_can_import_scales_bool ? 'True' : 'False'
        delete payload.admin_can_import_scales_bool

        payload.admin_can_edit_scales = this.form.admin_can_edit_scales_bool ? 'True' : 'False'
        delete payload.admin_can_edit_scales_bool

        payload.admin_can_delete_scales = this.form.admin_can_delete_scales_bool ? 'True' : 'False'
        delete payload.admin_can_delete_scales_bool
        
        await updateSettings(payload)
        this.$message.success('保存成功')
        // Refresh global store
        await this.$store.dispatch('fetchSettings')
        // Update document title immediately
        if (this.form.browser_title) {
          document.title = this.form.browser_title
        }
        
        // Exit edit mode
        this.isEditing = false
      } catch (e) {
        notifyApiError(e, '保存失败')
      } finally {
        this.saving = false
      }
    }
    ,
    openAgreementPublish() {
      this.agreementPublishForm = {
        version: '',
        title: 'AI 功能使用协议',
        content: '',
      }
      this.agreementDialogVisible = true
    },
    async publishAiAgreement() {
      if (!this.agreementPublishForm.version || !this.agreementPublishForm.content) {
        this.$message.error('请填写版本号与协议正文')
        return
      }
      this.agreementPublishing = true
      try {
        await publishAgreement({
          key: 'ai_usage',
          version: this.agreementPublishForm.version,
          title: this.agreementPublishForm.title,
          content: this.agreementPublishForm.content,
          is_active: true,
        })
        this.$message.success('发布成功（新版本生效后，用户下次使用需重新签署）')
        this.agreementDialogVisible = false
      } catch (e) {
        const msg = e.response?.data?.detail || '发布失败'
        notifyApiError(e, msg)
      } finally {
        this.agreementPublishing = false
      }
    },
    async viewCurrentAgreement() {
      this.currentAgreementDialogVisible = true
      this.currentAgreementLoading = true
      this.currentAgreement = null
      try {
        const resp = await getAgreementCurrent('ai_usage')
        this.currentAgreement = resp.data
      } catch (e) {
        // If 404, it means no agreement active, which is fine, handled by v-else
        if (e.response && e.response.status !== 404) {
          notifyApiError(e, '获取协议失败')
        }
      } finally {
        this.currentAgreementLoading = false
      }
    },
    formatDateTime(v) {
      if (!v) return ''
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return v
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.page {
  padding: 24px;
}
.card {
  background: var(--bg-card);
  padding: 24px;
  border-radius: 8px;
  max-width: 800px;
  margin: 0 auto;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 16px;
}
.title {
  font-size: 18px;
  font-weight: bold;
  color: var(--text-primary);
}
.section-title {
  font-size: 14px;
  font-weight: bold;
  color: var(--text-secondary);
  margin: 24px 0 16px 0;
  padding-left: 10px;
  border-left: 4px solid #409eff;
}
.form {
  max-width: 600px;
}
.upload-wrapper {
  display: flex;
  gap: 10px;
}
.preview {
  margin-top: 10px;
  background: #f0f0f0;
  padding: 5px;
  border-radius: 4px;
  display: inline-block;
}
.agreement-content {
  white-space: pre-wrap; 
  line-height: 1.7; 
  background: var(--bg-body); 
  color: var(--text-primary);
  padding: 16px; 
  border-radius: 4px; 
  border: 1px solid var(--border-color); 
  max-height: 500px; 
  overflow-y: auto;
}
</style>
